import { AlertOctagon, Home, RotateCcw } from "lucide-react";
import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { Footer } from "@/components/layout/auth-layout";
import { Logo } from "@/components/layout/logo";
import { Button } from "@/components/ui/button";

/** Shown instead of React Router's English default when a screen fails to render or load. */
export function ErrorPage() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  // A new release replaces the hashed screen files; reloading fetches the new ones.
  const staleChunk = error instanceof Error && /dynamically imported module|Importing a module script failed/i.test(error.message);
  return (
    <div className="flex min-h-dvh flex-col border-t-4 border-brand bg-ground">
      <header className="border-b border-line bg-surface/92 shadow-[0_8px_24px_-22px_rgb(16_30_45/0.55)] backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1440px] items-center px-4 sm:px-8"><Logo /></div>
      </header>
      <main className="flex flex-1 items-start justify-center px-3 py-10 sm:items-center">
        <div className="ui-section w-full max-w-lg p-6 text-center">
          <span className="mx-auto flex size-14 items-center justify-center rounded-md bg-bad-light text-bad">
            <AlertOctagon className="size-8" />
          </span>
          <h1 className="mt-3 text-lg font-bold">{notFound ? "ページが見つかりません" : staleChunk ? "新しい版に更新されました" : "画面を表示できませんでした"}</h1>
          <p className="mt-2 text-sm text-ink-2">
            {notFound
              ? "お探しのページは見つかりませんでした。URL をご確認ください。"
              : staleChunk
                ? "サービスが更新されたため、画面の読み込みに失敗しました。お手数ですが、ページを再読み込みしてください。"
                : "大変申し訳ございません。一時的な問題で画面を表示できませんでした。再読み込みしても解決しない場合は、管理者にお問い合わせください。"}
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Button variant="outline" onClick={() => window.location.reload()}><RotateCcw />再読み込み</Button>
            <Button onClick={() => (window.location.href = "/")}><Home />ホームへ戻る</Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/**
 * The same message inside the app's frame: the header and menu stay, so a
 * screen that fails never traps the user. A screen file missing after a
 * release reloads once by itself.
 */
export function InlineErrorPage() {
  const error = useRouteError();
  const staleChunk = error instanceof Error && /dynamically imported module|Importing a module script failed/i.test(error.message);
  const alreadyReloaded = (() => {
    try {
      return sessionStorage.getItem("eca.reloaded") === location.pathname;
    } catch {
      return true; // storage blocked: never reload by ourselves
    }
  })();
  useEffect(() => {
    if (!staleChunk) return;
    try {
      if (sessionStorage.getItem("eca.reloaded") === location.pathname) return;
      sessionStorage.setItem("eca.reloaded", location.pathname);
    } catch {
      return;
    }
    window.location.reload();
  }, [staleChunk]);
  return (
    <div className="flex justify-center py-10">
      <div className="ui-section w-full max-w-lg p-6 text-center">
        <span className="mx-auto flex size-14 items-center justify-center rounded-md bg-bad-light text-bad">
          <AlertOctagon className="size-8" />
        </span>
        <h1 className="mt-3 font-display text-lg font-bold">{staleChunk ? "新しい版に更新されました" : "この画面を表示できませんでした"}</h1>
        <p className="mt-2 text-sm text-ink-2">
          {staleChunk ? (alreadyReloaded ? "画面の読み込みに失敗しました。通信の状態をご確認のうえ、再読み込みしてください。" : "サービスが更新されたため、画面を読み込み直しています。") : "大変申し訳ございません。一時的な問題でこの画面を表示できませんでした。左のメニューからほかの画面へ移れます。再読み込みしても解決しない場合は、管理者にお問い合わせください。"}
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <Button variant="outline" onClick={() => window.location.reload()}><RotateCcw />再読み込み</Button>
        </div>
      </div>
    </div>
  );
}
